import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

export interface RouteCoordinates {
  lat: number;
  lng: number;
}

export interface SavedRoute {
  id: string;
  wellId: string;
  name: string;
  description?: string;
  coordinates: Array<[number, number]>;
  createdBy: string;
  createdAt: string;
}

export class LeafletMapService {
  private map: L.Map | null = null;
  private drawnItems: L.FeatureGroup | null = null;
  private drawControl: L.Control.Draw | null = null;
  private markers: (L.Marker | L.CircleMarker)[] = [];
  private routePolylines: Map<string, L.Polyline> = new Map();
  private onDrawComplete: ((coordinates: Array<[number, number]>) => void) | null = null;

  /**
   * Initialize Leaflet map with OpenStreetMap tiles
   */
  initializeMap(
    container: HTMLElement,
    center: { lat: number; lng: number } = { lat: 32.5, lng: -104.5 },
    zoom: number = 8
  ): L.Map {
    console.log('Initializing Leaflet map at', center, 'zoom', zoom);
    
    // Remove existing map if present
    if (this.map) {
      this.map.remove();
    }

    // Create map with dark theme
    this.map = L.map(container, {
      center: [center.lat, center.lng],
      zoom: zoom,
      preferCanvas: true
    } as any);

    console.log('Leaflet map created:', this.map);

    // Add OpenStreetMap tiles (dark style using CartoDB Positron)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
      className: 'leaflet-tile-dark'
    }).addTo(this.map);

    // Add satellite layer option
    const satelliteLayer = L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      {
        attribution: 'Tiles &copy; Esri',
        maxZoom: 20
      }
    );

    // Layer control
    L.control.layers(
      {
        'Terrain': L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 20
        }),
        'Satellite': satelliteLayer
      },
      {},
      { position: 'topright' }
    ).addTo(this.map);

    // Initialize drawn items feature group (for drawing)
    this.drawnItems = new L.FeatureGroup();
    this.map.addLayer(this.drawnItems);

    // Initialize draw control with only polyline tool
    this.drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polyline: {
          shapeOptions: {
            color: '#60a5fa',
            weight: 3,
            opacity: 0.8,
            dashArray: '5, 5',
            lineCap: 'round',
            lineJoin: 'round'
          },
          metric: true,
          feet: false,
          zIndexOffset: 2000
        },
        polygon: false,
        rectangle: false,
        circle: false,
        marker: false,
        circlemarker: false
      },
      edit: {
        featureGroup: this.drawnItems,
        remove: true
      }
    } as any);

    this.map.addControl(this.drawControl);

    // Handle draw completion
    this.map.on('draw:created', (event: any) => {
      const layer = event.layer;
      this.drawnItems!.addLayer(layer);

      if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
        const points = layer.getLatLngs() as L.LatLng[];
        const coordinates: Array<[number, number]> = points.map((point: L.LatLng) => [
          point.lat,
          point.lng
        ] as [number, number]);
        if (this.onDrawComplete) {
          this.onDrawComplete(coordinates);
        }
      }
    });

    // Handle draw editing
    this.map.on('draw:edited', (event: any) => {
      const layers = event.layers;
      layers.eachLayer((layer: any) => {
        if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
          const points = layer.getLatLngs() as L.LatLng[];
          const coordinates: Array<[number, number]> = points.map((point: L.LatLng) => [
            point.lat,
            point.lng
          ] as [number, number]);
          if (this.onDrawComplete) {
            this.onDrawComplete(coordinates);
          }
        }
      });
    });

    return this.map;
  }

  /**
   * Get current map instance
   */
  getMap(): L.Map | null {
    return this.map;
  }

  /**
   * Set callback for when drawing is completed
   */
  setOnDrawComplete(callback: (coordinates: Array<[number, number]>) => void): void {
    this.onDrawComplete = callback;
  }

  /**
   * Add well marker to map
   */
  addWellMarker(
    lat: number,
    lng: number,
    wellName: string,
    onClick?: () => void
  ): L.CircleMarker {
    if (!this.map) throw new Error('Map not initialized');

    const marker = L.circleMarker([lat, lng], {
      radius: 10,
      fillColor: '#fbbf24',
      color: '#ffffff',
      weight: 2,
      opacity: 1,
      fillOpacity: 0.9,
      pane: 'markerPane'
    } as any)
      .bindPopup(wellName)
      .addTo(this.map);

    if (onClick) {
      marker.on('click', onClick);
    }

    this.markers.push(marker);
    console.log('Added well marker at', lat, lng, wellName);
    return marker;
  }

  /**
   * Add search result marker to map
   */
  addSearchMarker(
    lat: number,
    lng: number,
    facilityName: string,
    onClick?: () => void
  ): L.CircleMarker {
    if (!this.map) throw new Error('Map not initialized');

    const marker = L.circleMarker([lat, lng], {
      radius: 8,
      fillColor: '#3b82f6',
      color: '#ffffff',
      weight: 1,
      opacity: 0.9,
      fillOpacity: 0.8,
      pane: 'markerPane'
    } as any)
      .bindPopup(facilityName)
      .addTo(this.map);

    if (onClick) {
      marker.on('click', onClick);
    }

    this.markers.push(marker);
    console.log('Added search marker at', lat, lng, facilityName);
    return marker;
  }

  /**
   * Clear all markers from map
   */
  clearMarkers(): void {
    this.markers.forEach(marker => {
      if (this.map) {
        this.map.removeLayer(marker);
      }
    });
    this.markers = [];
  }

  /**
   * Display saved route on map
   */
  displayRoute(route: SavedRoute): L.Polyline {
    if (!this.map) throw new Error('Map not initialized');

    const coordinates = route.coordinates.map(([lat, lng]) => [lat, lng] as [number, number]);

    const polyline = L.polyline(coordinates, {
      color: '#10b981',
      weight: 3,
      opacity: 0.7,
      dashArray: '10, 5',
      lineCap: 'round',
      lineJoin: 'round'
    })
      .bindPopup(`<strong>${route.name}</strong><br/>by ${route.createdBy}`)
      .addTo(this.map);

    this.routePolylines.set(route.id, polyline);
    return polyline;
  }

  /**
   * Remove route from map
   */
  removeRoute(routeId: string): void {
    if (!this.map) throw new Error('Map not initialized');

    const polyline = this.routePolylines.get(routeId);
    if (polyline) {
      this.map.removeLayer(polyline);
      this.routePolylines.delete(routeId);
    }
  }

  /**
   * Clear all routes from map
   */
  clearRoutes(): void {
    if (!this.map) throw new Error('Map not initialized');

    this.routePolylines.forEach(polyline => {
      this.map!.removeLayer(polyline);
    });
    this.routePolylines.clear();
  }

  /**
   * Fit map bounds to include all markers and routes
   */
  fitBounds(): void {
    if (!this.map || (this.markers.length === 0 && this.routePolylines.size === 0)) {
      return;
    }

    const group = new L.FeatureGroup();
    this.markers.forEach(marker => group.addLayer(marker));
    this.routePolylines.forEach(polyline => group.addLayer(polyline));

    this.map.fitBounds(group.getBounds(), { padding: [50, 50] });
  }

  /**
   * Enable/disable drawing mode
   */
  setDrawingEnabled(enabled: boolean): void {
    if (!this.drawControl || !this.map) return;

    if (enabled) {
      // Clear previous drawings
      this.drawnItems?.clearLayers();
      // Drawing is enabled by default when control is added
    } else {
      // Disable by removing draw mode
      this.drawnItems?.clearLayers();
    }
  }

  /**
   * Clear drawn items
   */
  clearDrawn(): void {
    if (this.drawnItems) {
      this.drawnItems.clearLayers();
    }
  }

  /**
   * Change map type (terrain/satellite/hybrid) - not fully implemented for Leaflet
   * Leaflet requires swapping entire tile layers
   */
  setMapType(_type: 'terrain' | 'satellite' | 'hybrid'): void {
    if (!this.map) return;
    // Leaflet doesn't have built-in layer switching like Google Maps
    // Layer control is added via L.control.layers() during initialization
  }

  /**
   * Cleanup map resources
   */
  destroy(): void {
    if (this.map) {
      this.clearMarkers();
      this.clearRoutes();
      this.map.remove();
      this.map = null;
    }
    this.drawnItems = null;
    this.drawControl = null;
    this.onDrawComplete = null;
  }
}

// Export singleton instance
export const leafletMapService = new LeafletMapService();
